import axios from 'axios';
import _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import useSWRInfinite from 'swr/infinite';

import {getDataQueryEndpointUrl} from './data-url-helpers';

/**
 * Builds a configuration object for fetching a list of entities using @see fetchEntityList.
 *
 * @param {object} [config]
 * Filter, sort, etc. configuration.
 *
 * @param {string} [config.filter]
 * The filter to apply when loading entities.
 *
 * @param {{field: string; dir: boolean}} [config.sort]
 * The sort to apply when loading entities.
 * - `sort.field` is the name of the field to sort on.
 * - `sort.dir` is the direction to sort in, `true` or `'asc'` for ascending, `false` or `'desc'` for descending.
 *
 * @param {string} [config.join]
 * The join to apply when loading entities.
 *
 * @param {string | string[]} [config.project]
 * The projection to apply when loading entities.
 *
 * @param {number} [config.pageSize]
 * The maximum number of entities to load.
 *
 * @param {string} [config.continuationToken]
 * The continuation token to use when loading entities.
 */
export const buildFetchEntityListOptions = (config = {}) => {
    const options = _.pick(config, ['filter', 'continuationToken']);

    if (config.sort) {
        const dir = {
            true: 'asc',
            false: 'desc',
            asc: 'asc',
            desc: 'desc',
        }[config.sort.dir.toString().toLowerCase()];
        options.orderBy = `${config.sort.field}:${dir}`;
    }

    if (config.join && !config.project) {
        options.join = config.join;
    }

    if (config.project) {
        if (Array.isArray(config.project)) {
            options.project = config.project.join(',');
        } else {
            options.project = config.project;
        }
    }

    if (config.pageSize >= 0) {
        options.pageSize = config.pageSize;
    }

    return options;
};

/**
 * Fetch a list of entities of the specified data type id or name using the specified `config`.
 *
 * @param {string} entityTypeIdOrName
 * The ID or name of the entity data type to fetch data for.
 *
 * @param {Object} config
 * The configuration object to use for fetching entities. 💡 Use @see buildFetchEntityListOptions to build this.
 *
 * @param {string} [config.filter]
 * The filter to apply when loading entities. 💡 Use `DataFilter` to build the filter string.
 *
 * @param {string} [config.orderBy]
 * The order to apply when loading entities. Should be in the format of `{field-name}:{dir}`, where `dir` is either `asc` or `desc`.
 *
 * @param {number} [config.pageSize]
 * The maximum number of entities to load.
 *
 * @param {string} [config.continuationToken]
 * The continuation token to use when loading entities.
 *
 * @param {string} [config.join]
 * The join to apply when loading entities. 💡 Use `project` instead for smaller and faster requests.
 *
 * @param {string} [config.project]
 * The projection to apply when loading entities.
 *
 * @returns {Promise<{continuation: string?; totalCount: number?; entities: Array<object>}>}
 * A promise that resolves to the response data.
 */
export const fetchEntityList = (entityTypeIdOrName, config) => {
    const url = getDataQueryEndpointUrl(entityTypeIdOrName);
    return axios.post(url, config).then((response) => response.data);
};

/**
 * `useEntityList` is a hook for loading a list of entities for a specified entity type.
 * It keeps track of loading state, errors, and exposes convenience functions for
 * updating loading configuration and loading more data.
 *
 * @param {number} entityTypeIdOrName
 * The ID or name of the entity data type to fetch data for.
 *
 * @param {object} [config]
 * Initial configuration for the hook.
 *
 * @param {string} [config.filter]
 * The initial filter to apply when loading entities.
 *
 * _Note that updating this prop will **NOT** cause the entity list to be reloaded.
 * For this purpose you must use the returned `.setFilter()` function._
 *
 * @param {{field: string; dir: boolean | 'asc' | 'desc'}} [config.sort]
 * The initial sort to apply when loading entities.
 *
 * _Note that updating this prop will **NOT** cause the entity list to be reloaded.
 * For this purpose, you must use the returned `.setSort()` function._
 *
 * - `sort.field` is the name of the field to sort on.
 * - `sort.dir` is the direction to sort in, eg. `true` for ascending, `false` for descending.
 *
 * @param {string} [config.join]
 * The join to apply when loading entities.
 *
 * @param {string} [config.project]
 * The projection to apply when loading entities.
 *
 * @param {number} [config.pageSize]
 * The maximum number of entities to load.
 *
 * @param {string} [config.continuationToken]
 * The initial continuation token to use when loading entities.
 *
 * _Note that updating this prop will **NOT** cause the entity list to be reloaded.
 * For this purpose, you must use the returned `.loadMore()` function._
 *
 * @param {boolean} [config.immutable]
 * Indicates whether to load the data once or keep track of changes.
 *
 * - `true`: load data once
 * - `false` (default): keep track of data becoming _stale_ and reload as needed
 */
export const useEntityList = (entityTypeIdOrName, config = {}) => {
    const [filter, setFilter] = useState(config?.filter);
    const [sort, setSort] = useState(config?.sort);
    const [isReloading, setIsReloading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const shouldRevalidate = config?.immutable !== true;

    const {
        // an array of the loaded pages; each item is the result of a single request
        data: pages,
        // a number that tells us how many pages are currently loaded
        size: pagesLoaded,
        // a function that sets how many pages should be displayed
        // automatically triggers loading the necessary data
        setSize: setPageSize,
        error,
        isLoading,
        isValidating,
        mutate,
    } = useSWRInfinite(
        (pageIndex, previousPageData) => {
            // This function builds the key for each page request.
            // SWR uses that key for deduping and caching.
            // Our key consists of the data type id or name that we want to fetch data for,
            // as well as the query configuration. Because in our use case a change in
            // the filter, for example, identifies a unique request.
            return [
                entityTypeIdOrName,
                buildFetchEntityListOptions({
                    ...config,
                    filter,
                    sort,
                    // if we're loading the first page use the continuation token from the config props (if any)
                    // otherwise, use the continuation token from the previous page's data (if any)
                    continuationToken: pageIndex === 0 ? config.continuationToken : previousPageData?.continuation,
                }),
            ];
        },
        ([, options]) => fetchEntityList(entityTypeIdOrName, options),
        {
            revalidateOnFocus: shouldRevalidate,
            revalidateOnReconnect: shouldRevalidate,
        },
    );

    useEffect(() => {
        if (!isValidating) {
            setIsReloading(false);
            setIsLoadingMore(false);
        }
    }, [isValidating]);

    const entities = useMemo(() => pages?.flatMap((x) => x.entities), [pages]);

    const loadMore = useCallback(() => {
        setIsLoadingMore(true);
        setPageSize(pagesLoaded + 1);
    }, [setPageSize, pagesLoaded]);

    const setSortIfChanged = useCallback(
        (sort) => {
            setSort((oldSort) => (_.isEqual(oldSort, sort) ? oldSort : sort));
        },
        [setSort],
    );

    const reload = useCallback(() => {
        setIsReloading(true);
        mutate();
    }, [mutate]);

    const continuation = pages?.at(-1)?.continuation;

    const totalCount = pages?.at(-1)?.totalCount;

    return useMemo(
        () => ({
            /** Returns the currently active filter configuration. */
            filter,

            /** Returns the currently active sort configuration. */
            sort,

            /** Returns the entities that have been loaded so far. */
            entities,

            /** Returns the amount of pages that have been loaded. */
            pagesLoaded,

            /**
             * @deprecated This exists for backwards compatibility.
             * Returns the same value as `isLoading`.
             *
             * Use one of the new, more descriptive properties:
             * - `isLoading`
             * - `isValidating`
             * - `isInitialLoading`
             * - `isLoadingMore`
             */
            loading: isLoading,

            /** Returns `true` when there's an ongoing request and no loaded data, yet. */
            isLoading,

            isReloading,

            /** Returns `true` when there's an ongoing request for loading more data. */
            isLoadingMore,

            /** Returns the error, if any, that was thrown when trying to load data. */
            error,

            /**
             * @deprecated This exists for backwards compatibility. Use the `loadMore()` function.
             */
            continuation,

            /** Returns the total number of entities that match the current filter. */
            totalCount,

            /** Updates the filter configuration for the entity list and triggers a reload. */
            setFilter,

            /** Updates the sort configuration for the entity list and triggers a reload. */
            setSort: setSortIfChanged,

            /** Loads an additional page of data. */
            loadMore,

            /** Reloads all the currently loaded data. */
            reload,
        }),
        [
            entities,
            error,
            filter,
            isLoading,
            isLoadingMore,
            isReloading,
            loadMore,
            continuation,
            totalCount,
            pagesLoaded,
            reload,
            sort,
        ],
    );
};
